/**
 * GT THRONE — Global JS (Critical UI)
 * Loads on every page via theme.liquid
 *
 * Behaviors:
 * 1. Theme toggle (dark/light)
 * 2. Scroll reveal (IntersectionObserver)
 * 3. Mobile nav overlay
 * 4. Floating menu button
 * 5. Cart drawer
 * 6. Popup (email capture)
 * 7. Smooth scroll
 * 8. Preloader (loading ring + percent)
 * 9. Scroll cursor (pointer follower + scroll-progress ring)
 */

(function () {
  'use strict';

  /* ---- 1. THEME TOGGLE ---- */
  (function initThemeToggle() {
    var saved = localStorage.getItem('gt-theme');
    if (saved === 'light') {
      document.documentElement.setAttribute('data-theme', 'light');
    }

    document.addEventListener('DOMContentLoaded', function () {
      var toggle = document.getElementById('themeToggle');
      if (!toggle) return;

      toggle.addEventListener('click', function () {
        var current = document.documentElement.getAttribute('data-theme');
        if (current === 'light') {
          document.documentElement.removeAttribute('data-theme');
          localStorage.setItem('gt-theme', 'dark');
        } else {
          document.documentElement.setAttribute('data-theme', 'light');
          localStorage.setItem('gt-theme', 'light');
        }
      });
    });
  })();

  /* ---- 2. SCROLL REVEAL ----
     .gt-reveal starts at opacity:0 (see base.css) and is revealed by adding
     .visible. The observer is created once and reused so we can also observe
     elements in sections that Shopify re-renders live in the theme editor.

     Theme editor gotcha: editing a section re-renders ONLY that section via the
     Section Rendering API — DOMContentLoaded does NOT fire again, so a freshly
     rendered section's .gt-reveal elements would stay invisible (blank section)
     until a full refresh. We listen for shopify:section:load and, in design
     mode, reveal immediately so live editing never shows a blank section. */
  (function initScrollReveal() {
    var inDesignMode = window.Shopify && window.Shopify.designMode;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    function revealIn(root) {
      var reveals = (root || document).querySelectorAll('.gt-reveal:not(.visible)');
      reveals.forEach(function (el) {
        if (inDesignMode) {
          // Skip the scroll-triggered animation in the editor so re-rendered
          // sections are visible instantly rather than sitting blank.
          el.classList.add('visible');
        } else {
          observer.observe(el);
        }
      });
    }

    document.addEventListener('DOMContentLoaded', function () { revealIn(document); });

    // Fired by Shopify when a section is added or re-rendered in the editor.
    document.addEventListener('shopify:section:load', function (e) {
      revealIn(e.target);
    });
  })();

  /* ---- 3. MOBILE NAV OVERLAY ---- */
  (function initMobileNav() {
    document.addEventListener('DOMContentLoaded', function () {
      var overlay = document.querySelector('.mobile-nav-overlay');
      if (!overlay) return;

      var toggleBtn = document.querySelector('.mobile-menu-toggle');
      var closeBtn = overlay.querySelector('.mobile-nav-close');
      var links = overlay.querySelectorAll('a');

      function open() {
        overlay.classList.add('active');
        document.body.style.overflow = 'hidden';
      }

      function close() {
        overlay.classList.remove('active');
        document.body.style.overflow = '';
      }

      if (toggleBtn) toggleBtn.addEventListener('click', open);
      if (closeBtn) closeBtn.addEventListener('click', close);
      links.forEach(function (link) {
        link.addEventListener('click', close);
      });
    });
  })();

  /* ---- 4. FLOATING MENU BUTTON ---- */
  (function initFloatingMenu() {
    document.addEventListener('DOMContentLoaded', function () {
      var wrapper = document.querySelector('.floating-menu-wrapper');
      if (!wrapper) return;

      var btn = document.getElementById('floatingMenuBtn');
      var dropdown = wrapper.querySelector('.floating-dropdown');

      window.addEventListener(
        'scroll',
        function () {
          if (window.scrollY > 300) {
            wrapper.classList.add('visible');
          } else {
            wrapper.classList.remove('visible');
            if (dropdown) dropdown.classList.remove('open');
          }
        },
        { passive: true }
      );

      if (btn && dropdown) {
        btn.addEventListener('click', function (e) {
          e.stopPropagation();
          dropdown.classList.toggle('open');
        });

        document.addEventListener('click', function () {
          dropdown.classList.remove('open');
        });
      }
    });
  })();

  /* ---- 5. CART DRAWER ---- */
  (function initCartDrawer() {
    document.addEventListener('DOMContentLoaded', function () {
      var cartToggle = document.getElementById('cartToggle');
      var cartDrawer = document.getElementById('cartDrawer');
      var cartOverlay = document.getElementById('cartOverlay');
      if (!cartToggle || !cartDrawer) return;

      function openCart() {
        cartDrawer.classList.add('active');
        if (cartOverlay) cartOverlay.classList.add('active');
        document.body.style.overflow = 'hidden';
      }

      function closeCart() {
        cartDrawer.classList.remove('active');
        if (cartOverlay) cartOverlay.classList.remove('active');
        document.body.style.overflow = '';
      }

      cartToggle.addEventListener('click', function (e) {
        e.preventDefault();
        openCart();
      });

      if (cartOverlay) cartOverlay.addEventListener('click', closeCart);

      var closeBtn = cartDrawer.querySelector('.cart-drawer-close');
      if (closeBtn) closeBtn.addEventListener('click', closeCart);
    });
  })();

  /* ---- 5b. CART BUBBLE REFRESH ----
     Keeps the header cart bubble in sync after AJAX add-to-cart from anywhere.
     Listens to:
       - 'gt:cart:refresh' (custom GT event, fired by gt-main-product.js)
       - PUB_SUB_EVENTS.cartUpdate (Dawn pub/sub, fired by product-form.js, cart.js, quick-add, etc.)
     Re-fetches the cart-icon-bubble section and replaces #cart-icon-bubble's inner HTML. */
  (function initCartBubbleRefresh() {
    var inFlight = false;
    var pending = false;

    function refresh() {
      if (inFlight) {
        pending = true;
        return;
      }
      inFlight = true;
      fetch('/?sections=cart-icon-bubble', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (!data) return;
          var html = data['cart-icon-bubble'];
          var anchor = document.getElementById('cart-icon-bubble');
          if (!html || !anchor) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var wrapper = doc.querySelector('.shopify-section') || doc.body;
          if (wrapper) anchor.innerHTML = wrapper.innerHTML;
        })
        .catch(function () { /* network/parse failure — leave bubble stale, will catch up next event */ })
        .finally(function () {
          inFlight = false;
          if (pending) {
            pending = false;
            refresh();
          }
        });
    }

    document.addEventListener('gt:cart:refresh', refresh);

    function bindPubSub() {
      if (typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, refresh);
        return true;
      }
      return false;
    }

    if (!bindPubSub()) {
      document.addEventListener('DOMContentLoaded', bindPubSub);
    }
  })();

  /* ---- 6. POPUP ----
     Reads behavior from data attributes set by sections/gt-popup.liquid:
       data-popup-delay           — seconds before auto-open
       data-popup-frequency       — 'always' | 'session' | 'days'
       data-popup-frequency-days  — cookie lifetime when frequency = 'days'
     If the section already opened the popup server-side (form post), we skip the timer
     so the success / error state stays visible. */
  (function initPopup() {
    var COOKIE_NAME = 'gt_popup_dismissed';
    var SESSION_KEY = 'gt_popup_seen';

    function hasCookie(name) {
      return document.cookie.split('; ').some(function (c) {
        return c.indexOf(name + '=') === 0;
      });
    }

    function setCookie(name, value, days) {
      var expires = new Date(Date.now() + days * 864e5).toUTCString();
      document.cookie =
        name + '=' + encodeURIComponent(value) +
        '; expires=' + expires +
        '; path=/; SameSite=Lax';
    }

    document.addEventListener('DOMContentLoaded', function () {
      var popup = document.getElementById('gtPopup');
      if (!popup) return;

      // Skip auto-open in the Shopify theme editor so it doesn't reopen on every
      // setting change / preview reload. Close handlers below still work for manual testing.
      var inDesignMode = window.Shopify && window.Shopify.designMode;

      var delay = parseInt(popup.getAttribute('data-popup-delay'), 10);
      if (isNaN(delay) || delay < 0) delay = 5;
      var frequency = popup.getAttribute('data-popup-frequency') || 'days';
      var frequencyDays = parseInt(popup.getAttribute('data-popup-frequency-days'), 10);
      if (isNaN(frequencyDays) || frequencyDays < 1) frequencyDays = 7;

      // Skip frequency gate if section already opened the popup server-side (success/error state).
      var alreadyOpen = popup.classList.contains('active');

      if (!alreadyOpen) {
        if (frequency === 'days' && hasCookie(COOKIE_NAME)) return;
        if (frequency === 'session') {
          try { if (sessionStorage.getItem(SESSION_KEY)) return; } catch (e) {}
        }
      }

      var closeBtn = popup.querySelector('.gt-popup__close');
      var dismissBtn = popup.querySelector('.gt-popup__dismiss');
      var overlay = popup.querySelector('.gt-popup__overlay');

      function markSeen() {
        if (frequency === 'days') setCookie(COOKIE_NAME, '1', frequencyDays);
        if (frequency === 'session') {
          try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) {}
        }
      }

      function closePopup() {
        popup.classList.remove('active');
        markSeen();
      }

      if (!alreadyOpen && !inDesignMode) {
        setTimeout(function () {
          popup.classList.add('active');
          markSeen();
        }, delay * 1000);
      }

      if (closeBtn) closeBtn.addEventListener('click', closePopup);
      if (dismissBtn) dismissBtn.addEventListener('click', closePopup);
      if (overlay) overlay.addEventListener('click', closePopup);
    });
  })();

  /* ---- 8. PRELOADER ---- */
  (function initPreloader() {
    var preloader = document.getElementById('gtPreloader');
    if (!preloader) return;

    // Skip preloader in Shopify theme editor to avoid blocking previews
    if (window.Shopify && window.Shopify.designMode) {
      preloader.classList.add('gt-preloader--done');
      return;
    }

    var ring = document.getElementById('gtPreloaderRing');
    var percentEl = document.getElementById('gtPreloaderPercent');
    var textEl = document.getElementById('gtPreloaderText');
    var circumference = 339.292; // 2πr for r=54
    var progress = 0;
    var done = false;

    var messages = [
      { at: 0, text: 'Preparing assets...' },
      { at: 25, text: 'Tuning haptics...' },
      { at: 50, text: 'Loading experience...' },
      { at: 75, text: 'Almost ready...' },
      { at: 95, text: 'Entering the throne...' }
    ];

    function setProgress(p) {
      p = Math.max(0, Math.min(100, p));
      if (ring) ring.style.strokeDashoffset = circumference - (circumference * p) / 100;
      if (percentEl) percentEl.textContent = Math.round(p) + '%';
      if (textEl) {
        for (var i = messages.length - 1; i >= 0; i--) {
          if (p >= messages[i].at) {
            if (textEl.textContent !== messages[i].text) textEl.textContent = messages[i].text;
            break;
          }
        }
      }
    }

    function finish() {
      if (done) return;
      done = true;
      setProgress(100);
      setTimeout(function () {
        preloader.classList.add('gt-preloader--done');
      }, 300);
    }

    // Simulate progress during load
    var tick = setInterval(function () {
      if (done) { clearInterval(tick); return; }
      progress = Math.min(progress + Math.random() * 8 + 2, 90);
      setProgress(progress);
    }, 120);

    function onReady() {
      clearInterval(tick);
      finish();
    }

    if (document.readyState === 'complete') {
      onReady();
    } else {
      window.addEventListener('load', onReady, { once: true });
      // Safety: hide after 6s regardless so a stalled asset doesn't block the UI
      setTimeout(onReady, 6000);
    }
  })();

  /* ---- 9. SCROLL CURSOR ---- */
  (function initScrollCursor() {
    var cursor = document.getElementById('gtScrollCursor');
    if (!cursor) return;

    // Skip on touch / small screens
    if (window.matchMedia('(hover: none)').matches || window.innerWidth < 768) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var fill = document.getElementById('gtScrollCursorFill');
    var circumference = 289.027; // 2πr for r=46
    var targetX = -100, targetY = -100;
    var currentX = -100, currentY = -100;
    var rafId = null;

    document.addEventListener('mousemove', function (e) {
      targetX = e.clientX - 24;
      targetY = e.clientY - 24;
      if (!cursor.classList.contains('is-visible')) cursor.classList.add('is-visible');
      if (!rafId) rafId = requestAnimationFrame(render);
    }, { passive: true });

    document.addEventListener('mouseleave', function () {
      cursor.classList.remove('is-visible');
    });

    function render() {
      currentX += (targetX - currentX) * 0.2;
      currentY += (targetY - currentY) * 0.2;
      cursor.style.transform = 'translate3d(' + currentX + 'px,' + currentY + 'px,0)';
      if (Math.abs(targetX - currentX) > 0.1 || Math.abs(targetY - currentY) > 0.1) {
        rafId = requestAnimationFrame(render);
      } else {
        rafId = null;
      }
    }

    function updateScrollProgress() {
      var doc = document.documentElement;
      var scrollable = doc.scrollHeight - doc.clientHeight;
      var p = scrollable > 0 ? (doc.scrollTop || window.pageYOffset) / scrollable : 0;
      p = Math.max(0, Math.min(1, p));
      if (fill) fill.style.strokeDashoffset = circumference - circumference * p;
    }

    window.addEventListener('scroll', updateScrollProgress, { passive: true });
    updateScrollProgress();
  })();

  /* ---- 7. SMOOTH SCROLL ---- */
  (function initSmoothScroll() {
    document.addEventListener('DOMContentLoaded', function () {
      var anchors = document.querySelectorAll('a[href*="#"]');
      anchors.forEach(function (anchor) {
        anchor.addEventListener('click', function (e) {
          var href = this.getAttribute('href') || '';
          var hashIndex = href.indexOf('#');
          var hash = href.slice(hashIndex);
          if (hash === '#' || hash.length < 2) return;

          // Only smooth-scroll when the link targets the current page (e.g. "#faq" or "/#faq" on the homepage).
          var path = href.slice(0, hashIndex);
          if (path && path !== window.location.pathname) return;

          var target;
          try { target = document.querySelector(hash); } catch (err) { return; }
          if (!target) return;

          e.preventDefault();
          var headerHeight = document.querySelector('.section-header')
            ? document.querySelector('.section-header').offsetHeight
            : 0;
          var top = target.getBoundingClientRect().top + window.scrollY - headerHeight;
          window.scrollTo({ top: top, behavior: 'smooth' });
          if (history.replaceState) history.replaceState(null, '', hash);
        });
      });
    });
  })();
})();
